import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { professionalsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import ImageUpload from '@/components/ImageUpload';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import {
  Stethoscope,
  MapPin,
  Calendar,
  Video,
  Home,
  CreditCard,
  ArrowRight,
  Loader2,
  CheckCircle,
  User,
  GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';

const specialties = [
  { id: 'general', name: 'Médecine Générale' },
  { id: 'pediatric', name: 'Pédiatrie' },
  { id: 'gynecology', name: 'Gynécologie' },
  { id: 'cardiology', name: 'Cardiologie' },
  { id: 'dermatology', name: 'Dermatologie' },
  { id: 'ophthalmology', name: 'Ophtalmologie' },
  { id: 'dentistry', name: 'Dentiste' },
  { id: 'physiotherapy', name: 'Kinésithérapie' },
  { id: 'nursing', name: 'Infirmier(e)' },
  { id: 'midwife', name: 'Sage-femme' },
  { id: 'psychology', name: 'Psychologie' },
  { id: 'nutrition', name: 'Nutrition' },
];

const ProfessionalSetup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    specialty: '',
    description: '',
    bio: '',
    license_number: '',
    diploma_url: '',
    profile_image: '',
    cover_image: '',
    address: '',
    latitude: null,
    longitude: null,
    services: [],
    consultation_fee: '',
    video_fee: '',
    home_visit_fee: '',
    availability: {},
    experience_years: '',
    education: '',
    languages: ['Français'],
  });

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const profileData = {
        ...formData,
        consultation_fee: parseFloat(formData.consultation_fee) || 0,
        video_fee: parseFloat(formData.video_fee) || 0,
        home_visit_fee: parseFloat(formData.home_visit_fee) || 0,
        experience_years: parseInt(formData.experience_years) || null,
      };

      await professionalsAPI.createProfile(profileData);
      setSuccess(true);
      toast.success('Profil créé avec succès!');
    } catch (error) {
      console.error('Error creating profile:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du profil');
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }));
          toast.success('Localisation récupérée');
        },
        () => {
          toast.error('Impossible de récupérer la localisation');
        }
      );
    }
  };

  if (success) {
    return (
      <div className="section-padding py-6">
        <div className="max-w-md mx-auto text-center py-12">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Profil créé avec succès!
          </h1>
          <p className="text-slate-600 mb-6">
            Votre profil est en cours de validation par notre équipe. Vous recevrez une notification une fois approuvé.
          </p>
          <Button
            onClick={() => navigate('/pro/dashboard')}
            className="bg-red-500 hover:bg-red-600 text-white rounded-full"
          >
            Aller au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="professional-setup-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Configurer votre profil
          </h1>
          <p className="text-slate-600 mt-2">
            Étape {step} sur 3
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-red-500 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 ${step > s ? 'bg-red-500' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-6">
            {step === 1 && (
              <div className="space-y-6">
                {/* Photo de profil */}
                <div className="flex flex-col items-center mb-6">
                  <Label className="mb-3">Photo de profil</Label>
                  <ImageUpload
                    currentImage={formData.profile_image}
                    onImageUploaded={(url) => setFormData({ ...formData, profile_image: url })}
                    type="profile"
                    size="lg"
                  />
                  <p className="text-xs text-slate-500 mt-2">Cliquez pour ajouter une photo</p>
                </div>

                <div>
                  <Label htmlFor="specialty">Spécialité *</Label>
                  <Select
                    value={formData.specialty}
                    onValueChange={(value) => setFormData({ ...formData, specialty: value })}
                  >
                    <SelectTrigger className="mt-2 h-12" data-testid="specialty-select">
                      <SelectValue placeholder="Choisir votre spécialité" />
                    </SelectTrigger>
                    <SelectContent>
                      {specialties.map((s) => (
                        <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="bio">À propos de vous</Label>
                  <Textarea
                    id="bio"
                    placeholder="Parlez de votre parcours, votre approche, vos spécialités..."
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    className="mt-2"
                    rows={4}
                    data-testid="bio-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="experience">Années d&apos;expérience</Label>
                    <Input
                      id="experience"
                      type="number"
                      placeholder="Ex: 10"
                      value={formData.experience_years}
                      onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                      className="mt-2 h-12"
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="license">Numéro de licence</Label>
                    <Input
                      id="license"
                      placeholder="Ex: MED-123456"
                      value={formData.license_number}
                      onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                      className="mt-2 h-12"
                      data-testid="license-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="education">Formation / Diplômes</Label>
                  <Input
                    id="education"
                    placeholder="Ex: Doctorat en médecine, Université de Yaoundé"
                    value={formData.education}
                    onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                    className="mt-2 h-12"
                  />
                </div>

                <Button
                  onClick={() => setStep(2)}
                  disabled={!formData.specialty}
                  className="w-full h-12 bg-red-500 hover:bg-red-600 text-white rounded-full"
                >
                  Continuer <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <Label>Services proposés *</Label>
                  <div className="space-y-3 mt-3">
                    <label className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:border-red-200 transition-colors">
                      <Checkbox
                        checked={formData.services.includes('consultation')}
                        onCheckedChange={() => handleServiceToggle('consultation')}
                        data-testid="service-consultation"
                      />
                      <Calendar className="w-5 h-5 text-red-500" />
                      <span>Consultation sur place</span>
                    </label>
                    <label className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:border-purple-200 transition-colors">
                      <Checkbox
                        checked={formData.services.includes('video_call')}
                        onCheckedChange={() => handleServiceToggle('video_call')}
                        data-testid="service-video"
                      />
                      <Video className="w-5 h-5 text-purple-600" />
                      <span>Téléconsultation (vidéo)</span>
                    </label>
                    <label className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:border-amber-200 transition-colors">
                      <Checkbox
                        checked={formData.services.includes('home_visit')}
                        onCheckedChange={() => handleServiceToggle('home_visit')}
                        data-testid="service-home"
                      />
                      <Home className="w-5 h-5 text-amber-600" />
                      <span>Visite à domicile</span>
                    </label>
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Adresse du cabinet</Label>
                  <div className="flex gap-2 mt-2">
                    <AddressAutocomplete
                      value={formData.address}
                      onChange={(value) => setFormData({ ...formData, address: value })}
                      onSelect={(location) => {
                        setFormData(prev => ({ 
                          ...prev, 
                          address: location.display_name,
                          latitude: location.lat,
                          longitude: location.lon
                        }));
                      }}
                      placeholder="Rechercher une adresse..."
                      className="flex-1"
                      data-testid="address-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={getLocation}
                      className="h-12"
                    >
                      <MapPin className="w-5 h-5" />
                    </Button>
                  </div>
                  {formData.latitude && (
                    <p className="text-xs text-emerald-600 mt-1">
                      Localisation GPS enregistrée
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12 rounded-full"
                  >
                    Retour
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={formData.services.length === 0}
                    className="flex-1 h-12 bg-red-500 hover:bg-red-600 text-white rounded-full"
                  >
                    Continuer <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <Label className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Tarifs (FCFA)
                  </Label>
                </div>

                {formData.services.includes('consultation') && (
                  <div>
                    <Label htmlFor="consultation_fee">Consultation sur place</Label>
                    <Input
                      id="consultation_fee"
                      type="number"
                      placeholder="Ex: 10000"
                      value={formData.consultation_fee}
                      onChange={(e) => setFormData({ ...formData, consultation_fee: e.target.value })}
                      className="mt-2 h-12"
                      data-testid="consultation-fee-input"
                    />
                  </div>
                )}

                {formData.services.includes('video_call') && (
                  <div>
                    <Label htmlFor="video_fee">Téléconsultation</Label>
                    <Input
                      id="video_fee"
                      type="number"
                      placeholder="Ex: 8000"
                      value={formData.video_fee}
                      onChange={(e) => setFormData({ ...formData, video_fee: e.target.value })}
                      className="mt-2 h-12"
                      data-testid="video-fee-input"
                    />
                  </div>
                )}

                {formData.services.includes('home_visit') && (
                  <div>
                    <Label htmlFor="home_fee">Visite à domicile</Label>
                    <Input
                      id="home_fee"
                      type="number"
                      placeholder="Ex: 15000"
                      value={formData.home_visit_fee}
                      onChange={(e) => setFormData({ ...formData, home_visit_fee: e.target.value })}
                      className="mt-2 h-12"
                      data-testid="home-fee-input"
                    />
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="flex-1 h-12 rounded-full"
                  >
                    Retour
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 h-12 bg-red-500 hover:bg-red-600 text-white rounded-full"
                    data-testid="submit-profile-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Création...
                      </>
                    ) : (
                      <>Créer mon profil</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfessionalSetup;
